/**
 * Wat er gaat gebeuren, vóórdat het gebeurt.
 *
 * Zie `docs/architecture/gelijktijdigheid.md` §7. Twee lezers, hetzelfde
 * scherm: de aanvrager krijgt 'm te zien vóór 'ie aankondigt (met een knop om
 * door te gaan), en wie de aftelling op z'n scherm heeft staan kan 'm erbij
 * halen om te kijken of z'n eigen werk erin voorkomt.
 *
 * De volgorde is niet willekeurig. **Wat verdwijnt staat bovenaan** — dat is
 * het enige wat je niet terugkrijgt door het opnieuw te doen. Daarna wat
 * overschreven wordt, dan de diagrammen, en pas onderaan wat erbij komt: een
 * getal, want nieuw neemt niemand iets af.
 */
import { useTranslation } from "react-i18next";
import type { OperationPreview, PreviewItem } from "../api/client";

function ItemList({ items, empty }: { items: PreviewItem[]; empty: string }) {
  if (items.length === 0) return <p className="text-(--ld-muted) text-xs italic">{empty}</p>;
  return (
    <ul className="max-h-48 overflow-y-auto rounded border border-(--ld-border) bg-(--ld-surface) text-xs">
      {items.map((item) => (
        <li
          key={item.id}
          className="flex gap-2 border-(--ld-rule) border-b px-2 py-1 last:border-0"
        >
          <span className="truncate">{item.name}</span>
          {item.typeId && <span className="ml-auto shrink-0 text-(--ld-muted)">{item.typeId}</span>}
        </li>
      ))}
    </ul>
  );
}

export function OperationPreviewDialog({
  preview,
  label,
  onConfirm,
  onCancel,
  busy,
}: {
  preview: OperationPreview;
  label: string;
  /** Weglaten maakt 't een kijkvenster in plaats van een vraag — zo ziet de
   *  ontvanger 'm. */
  onConfirm?: () => void;
  onCancel: () => void;
  busy?: boolean;
}) {
  const { t } = useTranslation();
  const removed = preview.removed_counts.concepts + preview.removed_counts.relations;
  const overwritten = preview.overwritten_counts.concepts + preview.overwritten_counts.relations;
  const created = preview.created.concepts + preview.created.relations + preview.created.diagrams;
  const diagramsRemoved = preview.diagrams.filter((d) => d.action === "remove");
  const diagramsTouched = preview.diagrams.filter((d) => d.action !== "remove");

  return (
    <div className="ledger fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
      <div className="flex max-h-full w-[42rem] max-w-full flex-col gap-3 overflow-y-auto rounded-lg border border-(--ld-control) bg-(--ld-raised) p-4 shadow-xl">
        <header>
          <h2 className="font-semibold text-(--ld-text) text-sm">
            {t(`operations.preview.title.${preview.kind}`)}
          </h2>
          <p className="text-(--ld-muted) text-xs">{label}</p>
        </header>

        {removed > 0 && (
          <section className="rounded border border-(--ld-control) bg-(--ld-negative-bg) p-2">
            <h3 className="mb-1 font-semibold text-(--ld-negative-ink) text-xs">
              {t("operations.preview.removed", { count: removed })}
            </h3>
            <ItemList items={preview.removed.concepts} empty={t("operations.preview.noneListed")} />
          </section>
        )}

        <section>
          <h3 className="mb-1 font-semibold text-(--ld-text) text-xs">
            {t("operations.preview.overwritten", { count: overwritten })}
          </h3>
          <ItemList
            items={preview.overwritten.concepts}
            empty={t("operations.preview.nothingOverwritten")}
          />
        </section>

        {(diagramsRemoved.length > 0 || diagramsTouched.length > 0) && (
          <section>
            <h3 className="mb-1 font-semibold text-(--ld-text) text-xs">
              {t("operations.preview.diagrams", { count: preview.diagrams.length })}
            </h3>
            <ul className="max-h-40 overflow-y-auto rounded border border-(--ld-border) bg-(--ld-surface) text-xs">
              {[...diagramsRemoved, ...diagramsTouched].map((d) => (
                <li
                  key={`${d.action}:${d.id}`}
                  className="flex gap-2 border-(--ld-rule) border-b px-2 py-1 last:border-0"
                >
                  <span className="truncate">{d.name}</span>
                  <span
                    className={`ml-auto shrink-0 ${
                      d.action === "remove"
                        ? "font-semibold text-(--ld-negative)"
                        : "text-(--ld-muted)"
                    }`}
                  >
                    {t(`operations.preview.diagramAction.${d.action}`)}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        <p className="text-(--ld-muted) text-xs">
          {t("operations.preview.created", { count: created })}
          {preview.reused > 0 && ` · ${t("operations.preview.reused", { count: preview.reused })}`}
        </p>

        {preview.truncated && (
          <p className="text-(--ld-muted) text-xs italic">{t("operations.preview.truncated")}</p>
        )}

        <footer className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="rounded border border-(--ld-control) bg-(--ld-surface) px-3 py-1 text-xs hover:bg-(--ld-raised) disabled:opacity-50"
          >
            {onConfirm ? t("operations.preview.cancel") : t("operations.preview.close")}
          </button>
          {onConfirm && (
            <button
              type="button"
              onClick={onConfirm}
              disabled={busy}
              className="rounded border border-transparent bg-(--ld-primary) px-3 py-1 text-(--ld-primary-ink) text-xs hover:bg-(--ld-primary-hover) disabled:bg-(--ld-disabled-bg) disabled:text-(--ld-disabled-ink)"
            >
              {t("operations.preview.confirm")}
            </button>
          )}
        </footer>
      </div>
    </div>
  );
}
